import * as React from 'react'
import { FormikProps } from 'formik'
import { FormikTextField, FormikSelect } from '..'
import { Grid, IconButton, Tooltip, Typography } from 'cs-kevlar-react'
import * as Yup from 'yup'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  getAllPermissionAccessTypes,
  getSupportUserPermissionLabel,
  getScopeLabel,
} from 'utils'
import {
  SupportUserSpreadPermissions,
  permissionMapping,
  PermissionType,
} from 'hooks/useSupportUserApi'
import {
  ApiPermissionsConfigurationViewModel,
  SupportUserPermission,
} from '../../api'
import { Security } from '@mui/icons-material'

interface EditSupportUserFormProps {
  formikProps: FormikProps<SupportUserSpreadPermissions>
  permissionConfiguration: ApiPermissionsConfigurationViewModel | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      minWidth: 0,
      alignItems: 'center',
    },
  }),
)

const getHelperTextForPermission = (
  permissionConfiguration: ApiPermissionsConfigurationViewModel | null,
  permission: SupportUserPermission,
) => {
  if (permissionConfiguration === null) return ''

  const label = getSupportUserPermissionLabel(permission)
  const scopes = permissionConfiguration.permissionScopeMap[label]

  if (!scopes) return ''

  return scopes.map(getScopeLabel).join(', ')
}

export const EditSupportUserFormSchema = Yup.object().shape({
  username: Yup.string().required('The username field is required.'),
})

const accessOptions = getAllPermissionAccessTypes()

export const EditSupportUserForm: React.FunctionComponent<EditSupportUserFormProps> = ({
  formikProps,
  permissionConfiguration,
}) => {
  const classes = useStyles({})

  return (
    <React.Fragment>
      <Grid item className={classes.gridItem}>
        <FormikTextField
          formikProps={formikProps}
          fieldName="username"
          label="Username"
          fullWidth
          readOnly={true}
          required
        />
      </Grid>
      {Object.keys(permissionMapping).map((permissionType) => {
        const permissionDefinition =
          permissionMapping[permissionType as PermissionType]
        const selectedValue = (formikProps.values as any)[permissionType]
        let helperText = ''
        if (selectedValue !== 'None') {
          const permission = (permissionDefinition as any)[
            selectedValue.toLowerCase()
          ] as SupportUserPermission
          helperText = getHelperTextForPermission(
            permissionConfiguration,
            permission,
          )
        }
        return (
          <Grid
            item
            className={classes.gridItem}
            key={`EditSupportUser::${permissionType}`}
          >
            <FormikSelect
              id={`EditSupportUser::${permissionType}`}
              formikProps={formikProps}
              label={permissionMapping[permissionType as PermissionType].label}
              fieldName={permissionType}
              fullWidth
              defaultValue={'None'}
              options={accessOptions}
            />
            <Tooltip
              placement="left"
              title={helperText}
              disableHoverListener={helperText === ''}
              disableFocusListener={helperText === ''}
              disableTouchListener={helperText === ''}
            >
              <IconButton
                aria-label="View scopes for permission"
                disabled={helperText === ''}
                size="large">
                <Security />
              </IconButton>
            </Tooltip>
          </Grid>
        );
      })}
    </React.Fragment>
  );
}
